/**
 * Переходы обучения: первый вход, диагностика, её итог и тренировка.
 *
 * Вынесены из корневого хука переходов: тот перерос порог, за которым файл
 * перестают читать целиком (ТЗ NFR-Q-02).
 */

import { useCallback } from 'react';
import type { TypingSession } from '@type2x/typing-engine';
import type { OnboardingChoice } from '@type2x/feature-onboarding';
import type { Workspace } from '@type2x/feature-personal-workspace';

import type { Runtime } from '../runtime.js';
import type { AppScreen } from '../state/app-state.js';
import {
  finishDiagnostics,
  finishPractice,
  practiceScreen,
  resumeDiagnostics,
  startFromOnboarding,
} from '../state/flow.js';
import { fundamentalsScreen } from '../state/fundamentals.js';
import type { useSync } from './useSync.js';
import type { AppFlow, WorkspaceAccess } from './useAppFlow.js';

export function useLearningActions(
  runtime: Runtime,
  access: WorkspaceAccess,
  sync: ReturnType<typeof useSync>,
  setScreen: (screen: AppScreen) => void,
  screen: AppScreen,
): Pick<
  AppFlow,
  | 'onOnboardingDone'
  | 'onDiagnosticsDone'
  | 'onContinueAfterDiagnostics'
  | 'onRetakeDiagnostics'
  | 'onPracticeDone'
> {
  const { workspace, commit } = access;

  const onOnboardingDone = useCallback(
    (choice: OnboardingChoice) => {
      const transition = startFromOnboarding(runtime, choice);
      void commit(transition.workspace);

      // Новичок начинает с основ: диагностика измеряла бы у него то, чего
      // ещё нет (ТЗ §6, режим 2).
      setScreen(
        choice.start === 'fundamentals'
          ? fundamentalsScreen(runtime, transition.workspace)
          : transition.screen,
      );
    },
    [runtime, commit, setScreen],
  );

  const onDiagnosticsDone = useCallback(
    (words: readonly string[], session: TypingSession) => {
      if (workspace === null) return;
      const transition = finishDiagnostics(runtime, workspace, words, session);
      void commit(transition.workspace);
      setScreen(transition.screen);
    },
    [runtime, workspace, commit],
  );

  const onPracticeDone = useCallback(
    (session: TypingSession) => {
      if (workspace === null || screen.name !== 'practice') return;

      const transition = finishPractice(runtime, workspace, screen.plan, session, screen.sessionId);
      void commit(transition.workspace);
      setScreen(transition.screen);

      // Отправка идёт после показа результата: сеть не задерживает экран.
      if (transition.sessionPayload !== undefined) {
        void sync.submit(transition.sessionPayload);
      }
    },
    [runtime, workspace, screen, commit, sync],
  );

  return {
    onOnboardingDone,
    onDiagnosticsDone,
    onPracticeDone,
    ...useDiagnosticResultActions(runtime, workspace, setScreen),
  };
}

/**
 * Куда ведёт экран итога диагностики.
 *
 * Рекомендация — совет, а не развилка кода: «продолжить» ведёт в тренировку,
 * а «начать с основ» стоит на экране отдельной кнопкой. Так человек видит оба
 * пути, а не тот, который выбрали за него (ТЗ FR-EN-55).
 */
function useDiagnosticResultActions(
  runtime: Runtime,
  workspace: Workspace | null,
  setScreen: (screen: AppScreen) => void,
): Pick<AppFlow, 'onContinueAfterDiagnostics' | 'onRetakeDiagnostics'> {
  const onContinueAfterDiagnostics = useCallback(() => {
    if (workspace === null) return;
    setScreen(practiceScreen(runtime, workspace));
  }, [runtime, workspace, setScreen]);

  // Повторное измерение ничего не стирает: baseline перезапишется итогом
  // нового прогона, а освоенность клавиш накопилась и остаётся.
  const onRetakeDiagnostics = useCallback(() => {
    if (workspace === null) return;
    setScreen(resumeDiagnostics(runtime, workspace));
  }, [runtime, workspace, setScreen]);

  return { onContinueAfterDiagnostics, onRetakeDiagnostics };
}
